import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  Text,
  Icon,
  HStack,
} from '@chakra-ui/react';
import { ViewIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';

interface ShareAllConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  unitName?: string;
  isLoading?: boolean;
  closeOnOverlayClick?: boolean;
}

const bgColor = '#ffffff';
const borderColor = '#e2e8f0';

export const ShareAllConfirmModal: React.FC<ShareAllConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  unitName,
  isLoading = false,
  closeOnOverlayClick = true,
}) => {
  const { t } = useTranslation();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      isCentered
      closeOnOverlayClick={closeOnOverlayClick}
      closeOnEsc={closeOnOverlayClick}
    >
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent
        bg={bgColor}
        borderRadius="2xl"
        boxShadow="2xl"
        maxW="500px"
      >
        <ModalHeader
          pb={4}
          borderBottom="1px solid"
          borderColor={borderColor}
        >
          <HStack spacing={3}>
            <Icon as={ViewIcon} color="blue.500" w={6} h={6} />
            <VStack align="start" spacing={0}>
              <Text fontSize="xl" fontWeight="bold" color="#2d3748">
                {t('class.shareAllConfirm.title')}
              </Text>
              <Text fontSize="sm" color="gray.500">
                {t('class.shareAllConfirm.description')}
              </Text>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody py={6}>
          <Text fontSize="md" color="#4a5568" lineHeight="1.5">
            {unitName
              ? t('class.shareAllConfirm.messageWithUnit', { unit: unitName })
              : t('class.shareAllConfirm.message')}
          </Text>
        </ModalBody>

        <ModalFooter
          pt={4}
          borderTop="1px solid"
          borderColor={borderColor}
        >
          <HStack spacing={3} w="full" justify="flex-end">
            <Button
              variant="ghost"
              onClick={onClose}
              size="lg"
              px={8}
              isDisabled={isLoading}
            >
              {t('general.cancel')}
            </Button>
            <Button
              colorScheme="blue"
              onClick={onConfirm}
              isLoading={isLoading}
              loadingText={t('class.shareAllConfirm.sharing')}
              size="lg"
              px={8}
              borderRadius="xl"
              leftIcon={<ViewIcon />}
              _hover={{
                transform: 'translateY(-1px)',
                boxShadow: 'lg',
              }}
              transition="all 0.2s"
            >
              {t('class.shareAllConfirm.confirmButton')}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
